/**
 * Amplience product-carousel async strip bootstrap.
 *
 * Loads `Cms-GetProductStrip` HTML into product carousel async containers,
 * initializes Slick with product-strip settings, and fetches remaining
 * placeholder tiles on carousel init.
 */
'use strict';

const BREAKPOINT_MOBILE = 768;
const BREAKPOINT_TABLET = 1024;
const ARROW_SVG_PATH =
    'M7.55039 13.1L8.59492 12.092L4.97849 8.59251H15.6504V7.1661' +
    'H4.97849L8.59492 3.68566L7.55039 2.66815L2.15039 7.87931L7.55039 13.1Z';

/**
 * Builds prev/next arrow markup for the legacy product strip carousel.
 *
 * @param {'prev'|'next'} direction - Slick control direction.
 * @returns {string} HTML for the arrow button.
 */
function buildProductStripArrow(direction) {
    const arrowClass = `product-carousel__arrow product-carousel__arrow-${direction} slick-${direction} slick-arrow slick-arrow--center`;
    const resourceKey = direction === 'next' ? 'GO_NEXT' : 'GO_PREV';
    const label =
        (window.Resources && window.Resources[resourceKey]) ||
        (direction === 'next' ? 'Next' : 'Previous');
    const iconStyle =
        direction === 'next' ? ' style="transform:rotate(180deg)"' : '';

    return `<button type="button" class="${arrowClass}" title="${label}">
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="currentColor" aria-hidden="true" focusable="false"${iconStyle}>
            <path d="${ARROW_SVG_PATH}" fill="currentColor" />
        </svg>
        <span class="visually-hidden">${label}</span>
    </button>`;
}

/**
 * Adds the agreed QA selectors to actual product carousel item elements.
 *
 * @param {HTMLElement} root - Loaded product strip root.
 * @returns {void}
 */
function applyProductCarouselItemTestIds(root) {
    const items = root.querySelectorAll(
        '.product-carousel__item:not(.product-carousel__item--placeholder)',
    );

    for (let i = 0; i < items.length; i++) {
        items[i].setAttribute('data-testid', `carousel-product-${i}`);
    }
}

/**
 * Keeps carousel container test IDs unique when multiple modules render.
 *
 * @returns {void}
 */
function applyProductCarouselContainerTestIds() {
    const carousels = document.querySelectorAll(
        '.productCarousel[data-product-carousel]',
    );

    for (let i = 0; i < carousels.length; i++) {
        carousels[i].setAttribute('data-testid', `product-carousel-${i}`);
    }
}

/**
 * Fetches remaining product-strip tiles into placeholder slides.
 *
 * @param {Event} evt - Slick init event.
 * @param {Object} slickInstance - Slick instance.
 * @returns {void}
 */
function loadNextCarouselItems(evt, slickInstance) {
    const carousel = evt.currentTarget;
    const nextItemsUrl = carousel.getAttribute('data-next-items-url');
    const itemsCount =
        parseInt(carousel.getAttribute('data-items-count'), 10) || 0;

    if (!nextItemsUrl || !slickInstance) return;

    jQuery.get(nextItemsUrl, (response) => {
        const itemsWrapper = document.createElement('div');

        itemsWrapper.innerHTML = response;

        const tiles = itemsWrapper.querySelectorAll(
            '.product-carousel__item:not(.product-carousel__item--placeholder)',
        );

        for (let i = 0; i < tiles.length; i++) {
            const slideIndex = itemsCount + i;
            const slideEl = slickInstance.$slides.get(slideIndex);

            if (slideEl) {
                slideEl.innerHTML = tiles[i].innerHTML;
                slideEl.classList.remove('product-carousel__item--placeholder');
            }
        }

        applyProductCarouselItemTestIds(carousel);

        if (tiles.length) {
            if (window.initModules) {
                window.initModules();
            }
            if (window.initTiles) {
                window.initTiles();
            }
        }
    });
}

/**
 * Initializes Slick on a product-strip carousel list.
 *
 * @param {jQuery} $carousel - jQuery wrapper for `ul.product-carousel`.
 * @returns {void}
 */
function initProductStripCarousel($carousel) {
    if (!$carousel.length || !jQuery.fn.slick) return;

    if ($carousel.hasClass('slick-initialized')) return;

    const slickConfig = {
        arrows: true,
        dots: true,
        slidesToShow: 5,
        slidesToScroll: 1,
        infinite: false,
        waitForAnimate: false,
        rows: 0,
        cssEase: 'ease-in-out',
        lazyLoad: 'progressive',
        slide: '.product-carousel__item',
        prevArrow: buildProductStripArrow('prev'),
        nextArrow: buildProductStripArrow('next'),
        responsive: [
            {
                breakpoint: BREAKPOINT_TABLET,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: BREAKPOINT_MOBILE,
                settings: {
                    arrows: false,
                    dots: true,
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    variableWidth: true,
                },
            },
        ],
    };

    const rawSlick = $carousel.attr('data-slick');

    if (rawSlick) {
        $carousel.attr('data-carousel-config', rawSlick);
    }

    $carousel.attr('data-slick', '');

    $carousel.on('init', (evt, slickInstance) => {
        $carousel.removeAttr('data-slick');
        loadNextCarouselItems(evt, slickInstance);
    });

    $carousel.slick(slickConfig);
}

/**
 * Initializes all product-strip carousels inside loaded strip markup.
 *
 * @param {HTMLElement} stripRoot - Async strip container.
 * @returns {void}
 */
function initCarouselsInStrip(stripRoot) {
    const carousels = stripRoot.querySelectorAll(
        'ul.product-carousel[data-slick]',
    );

    for (let i = 0; i < carousels.length; i++) {
        initProductStripCarousel(jQuery(carousels[i]));
    }
}

/**
 * Loads product-strip HTML and initializes its carousels.
 *
 * @param {HTMLElement} container - Element with `data-async-component` config.
 * @returns {void}
 */
function loadProductStrip(container) {
    const rawConfig = container.getAttribute('data-async-component');

    if (!rawConfig) return;

    let config;

    try {
        config = JSON.parse(rawConfig);
    } catch (e) {
        return;
    }

    container.removeAttribute('data-async-component');

    if (!config.url) return;

    jQuery
        .ajax({
            url: config.url,
            type: 'GET',
            dataType: 'html',
        })
        .done((html) => {
            container.innerHTML = html;

            if (!container.querySelector('.product-tile')) {
                const isPreview =
                    window.location.search.indexOf('vse=') !== -1 ||
                    window.location.pathname.indexOf(
                        'Amplience-PreviewPage',
                    ) !== -1;

                if (!isPreview) {
                    const section = container.closest('.productCarousel');

                    if (section) {
                        section.hidden = true;
                    }
                }

                return;
            }

            initCarouselsInStrip(container);
            applyProductCarouselItemTestIds(container);
            if (window.initModules) {
                window.initModules();
            }
            if (window.initTiles) {
                window.initTiles();
            }
        });
}

/**
 * Bootstraps product-carousel async strips.
 *
 * @returns {void}
 */
export function initProductCarouselStrips() {
    if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.slick) {
        return;
    }

    const containers = document.querySelectorAll(
        '.productCarousel__strip[data-async-component]',
    );

    applyProductCarouselContainerTestIds();

    for (let i = 0; i < containers.length; i++) {
        loadProductStrip(containers[i]);
    }
}
